/**
 * A calculation, as a person would describe it rather than as the expression
 * language would.
 *
 * The designer has always been able to *bind* a value — point a property at a
 * field — and never to work one out. Arithmetic arrived only inside a template
 * somebody wrote by hand, so a host with the expression editor switched off had a
 * document whose totals it could read and not change, and a host with it on asked
 * a business user to write `sum(invoice.lineItems.amount) * (1 + taxRate)`. Both
 * are the same missing feature.
 *
 * So: a small closed model of the arithmetic documents actually contain, and a
 * codec between it and the expression language. The builder in the editor edits
 * this; the template still holds an ordinary expression, so nothing downstream —
 * the interpreter, the validator, the renderer, the PDF backends — learns anything
 * new. A calculation is a *view* of an expression, never a second way to store one.
 *
 * ## Left to right, and no brackets
 *
 * `start` and then a list of `steps`, each applied to the running total. That is
 * the one decision here worth defending, because a tree would be more general and
 * much worse: an interface with brackets in it is a formula editor, and a business
 * user with a formula editor is a business user writing formulas. Left-to-right
 * covers what invoices and reports do — a subtotal, VAT on it, a discount off it,
 * a division for an average — and reads as a sentence:
 *
 *     Total of Lines › Amount, add VAT rate as a percentage
 *
 * Precedence never arises, because there is none to get wrong: every step applies
 * to everything before it, and `toExpression` brackets accordingly.
 *
 * ## Percentages are an operator, not multiplication
 *
 * "Add VAT rate as a percentage" rather than "multiply by (1 + VAT rate)". It is
 * the same arithmetic and it is the difference between a step somebody can choose
 * and one they have to derive. It is also emitted null-safe — `coalesce(rate, 0)`
 * — because a business that has not set a VAT rate wants an invoice without VAT,
 * not one with a hole where the total was.
 *
 * ## The contract that makes this safe
 *
 * {@link fromExpression} returns `null` for anything it cannot rebuild **byte for
 * byte**. An interface that offered to edit an expression it would then rewrite
 * differently is an interface that silently changes documents on being looked at;
 * refusing is the honest answer, and the caller shows the value as read-only
 * instead. `calculation.test.ts` asserts the round trip on every expression the
 * starter templates ship.
 */
import { exprToString, type Expr } from "../expr/ast.js";
import { parseExpression } from "../expr/parser.js";

/** The five aggregates a document asks for. Deliberately not every function. */
export type CalcAggregate = "sum" | "avg" | "count" | "min" | "max";

/** One thing a calculation reads or holds. Flat: a term is never itself a calculation. */
export type CalcTerm =
  /** A field, by its dotted path. `invoice.taxRate`, `row.amount`. */
  | { kind: "field"; path: string }
  /** A column of a list, across every row of it. `sum(invoice.lineItems.amount)`. */
  | { kind: "aggregate"; fn: CalcAggregate; path: string }
  /** A number somebody typed. */
  | { kind: "number"; value: number };

/**
 * What to do with a term, given everything so far.
 *
 * `addPercent` and `subtractPercent` treat the term as a rate: 0.2 is a fifth.
 * That matches `percent`-typed fields and the mapping package's own reading of a
 * typed rate, so a VAT rate is the same number everywhere in the product.
 */
export type CalcOp = "add" | "subtract" | "multiply" | "divide" | "addPercent" | "subtractPercent";

export interface CalcStep {
  op: CalcOp;
  term: CalcTerm;
}

export interface Calculation {
  start: CalcTerm;
  steps: CalcStep[];
  /**
   * How the result is shown: a pipe chain, exactly as it appears after `|` in the
   * expression. `currency:"GBP"`, `number:0`, `percent:1`. Presentation only, and
   * applied once at the end — a format in the middle of arithmetic would turn a
   * number into a string and the next step into a concatenation.
   */
  format?: string;
}

/** Everything a calculation reads, for validation and for naming it. */
export function calcPaths(calc: Calculation): string[] {
  const out: string[] = [];
  const add = (term: CalcTerm) => {
    if (term.kind === "field" || term.kind === "aggregate") out.push(term.path);
  };
  add(calc.start);
  for (const step of calc.steps) add(step.term);
  return out;
}

/* ------------------------------ to expression ----------------------------- */

function termAst(term: CalcTerm): Expr {
  if (term.kind === "number") return { kind: "literal", value: term.value };
  const path: Expr = { kind: "path", segments: term.path.split(".").map((name) => ({ kind: "prop", name })) };
  return term.kind === "aggregate" ? { kind: "call", name: term.fn, args: [path] } : path;
}

/** `coalesce(x, 0)`, so an unset rate means "no VAT" rather than a broken total. */
function safe(term: CalcTerm): Expr {
  if (term.kind === "number") return termAst(term);
  return { kind: "call", name: "coalesce", args: [termAst(term), { kind: "literal", value: 0 }] };
}

function applyStep(left: Expr, step: CalcStep): Expr {
  switch (step.op) {
    case "add":
      return { kind: "binary", op: "+", left, right: termAst(step.term) };
    case "subtract":
      return { kind: "binary", op: "-", left, right: termAst(step.term) };
    case "multiply":
      return { kind: "binary", op: "*", left, right: termAst(step.term) };
    case "divide":
      return { kind: "binary", op: "/", left, right: termAst(step.term) };
    case "addPercent":
      return { kind: "binary", op: "*", left, right: { kind: "binary", op: "+", left: { kind: "literal", value: 1 }, right: safe(step.term) } };
    case "subtractPercent":
      return { kind: "binary", op: "*", left, right: { kind: "binary", op: "-", left: { kind: "literal", value: 1 }, right: safe(step.term) } };
  }
}

/** The AST, for a caller that wants to type-check a calculation before storing it. */
export function calcToAst(calc: Calculation): Expr {
  let ast = termAst(calc.start);
  for (const step of calc.steps) ast = applyStep(ast, step);
  if (!calc.format) return ast;
  // The format is a pipe chain and may be more than one pipe, so it is parsed
  // rather than assembled: `currency:"GBP":0` has arguments, and hand-building
  // those here would be a second, worse parser.
  const piped = parseExpression(`__x__ | ${calc.format}`);
  return graft(piped, ast);
}

/** Replaces the `__x__` placeholder at the bottom of a parsed pipe chain. */
function graft(piped: Expr, input: Expr): Expr {
  if (piped.kind === "pipe") return { ...piped, input: graft(piped.input, input) };
  return input;
}

export function toExpression(calc: Calculation): string {
  return exprToString(calcToAst(calc));
}

/* ----------------------------- from expression ---------------------------- */

const AGGREGATES: CalcAggregate[] = ["sum", "avg", "count", "min", "max"];

function asTerm(e: Expr): CalcTerm | null {
  if (e.kind === "literal" && typeof e.value === "number") return { kind: "number", value: e.value };
  if (e.kind === "path" && e.segments.every((s) => s.kind === "prop")) {
    return { kind: "field", path: e.segments.map((s) => (s.kind === "prop" ? s.name : "")).join(".") };
  }
  if (e.kind === "call" && AGGREGATES.includes(e.name as CalcAggregate) && e.args.length === 1) {
    const inner = asTerm(e.args[0]!);
    if (inner?.kind === "field") return { kind: "aggregate", fn: e.name as CalcAggregate, path: inner.path };
  }
  return null;
}

/** Unwraps the `coalesce(x, 0)` a percentage step is emitted with. */
function asSafeTerm(e: Expr): CalcTerm | null {
  if (e.kind === "call" && e.name === "coalesce" && e.args.length === 2) {
    const fallback = e.args[1]!;
    if (fallback.kind === "literal" && fallback.value === 0) return asTerm(e.args[0]!);
    return null;
  }
  return asTerm(e);
}

/**
 * Whether a right-hand side is `1 + rate` or `1 - rate`, which is what a
 * percentage step looks like once it is arithmetic.
 */
function asPercent(e: Expr): { op: "addPercent" | "subtractPercent"; term: CalcTerm } | null {
  if (e.kind !== "binary" || (e.op !== "+" && e.op !== "-")) return null;
  if (e.left.kind !== "literal" || e.left.value !== 1) return null;
  const term = asSafeTerm(e.right);
  if (!term) return null;
  return { op: e.op === "+" ? "addPercent" : "subtractPercent", term };
}

const OPS: Record<string, CalcOp> = { "+": "add", "-": "subtract", "*": "multiply", "/": "divide" };

/**
 * Reads an expression back as a calculation, or refuses.
 *
 * `null` means "this is not something the builder can represent", and the caller's
 * job is then to show the value rather than offer to edit it. Everything this
 * accepts, {@link toExpression} reproduces character for character — which is
 * asserted, because the failure otherwise is a template quietly rewritten by
 * somebody opening a panel.
 */
export function fromExpression(expr: string): Calculation | null {
  let ast: Expr;
  try {
    ast = parseExpression(expr);
  } catch {
    return null;
  }

  // The format comes off the top: every pipe in the chain, in source form.
  const formats: string[] = [];
  while (ast.kind === "pipe") {
    formats.unshift(`${ast.name}${ast.args.length ? ":" + ast.args.map(exprToString).join(":") : ""}`);
    ast = ast.input;
  }
  const format = formats.length ? formats.join(" | ") : undefined;

  // Then the left spine, innermost first.
  const steps: CalcStep[] = [];
  // Annotated, because the loop above narrows `ast` to "not a pipe" and an
  // inferred `cursor` would then refuse the wider node its own descent produces.
  let cursor: Expr = ast;
  while (cursor.kind === "binary") {
    const op = OPS[cursor.op];
    if (!op) return null;
    if (op === "multiply") {
      const percent = asPercent(cursor.right);
      if (percent) {
        steps.unshift(percent);
        cursor = cursor.left;
        continue;
      }
    }
    const term = asTerm(cursor.right);
    if (!term) return null;
    steps.unshift({ op, term });
    cursor = cursor.left;
  }

  const start = asTerm(cursor);
  if (!start) return null;

  const calc: Calculation = { start, steps, ...(format ? { format } : {}) };
  // The whole point. Anything that does not come back identically is refused,
  // however close it looked.
  return toExpression(calc) === expr.trim() ? calc : null;
}

/** Whether an expression is arithmetic rather than a plain field or a formatted one. */
export function isCalculation(calc: Calculation): boolean {
  return calc.steps.length > 0 || calc.start.kind === "aggregate";
}
